import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule, FormsModule, Validators, FormBuilder } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { LoginService } from '../../core/services/login.service';
import { NotificationService } from '../../core/services/notification.service';
import { LoginResponseModel } from '../../core/models/login-response.model';
import { DoctorProfileService } from '../../core/services/doctor-profile.service';
import { DoctorProfileStatus } from '../../core/enums/doctor-profile-status.enum';
import { PermissionService } from '../../core/services/permission.service';
import { UserRole, UserRolePermissions } from '../../core/enums/user-role.enum';
import { ClinicService } from '../../core/services/clinic.service';

@Component({
  selector: 'app-otp-login',
  imports: [CommonModule, ReactiveFormsModule, FormsModule, RouterLink],
  templateUrl: './otp-login.html',
  styleUrl: './otp-login.scss',
})
export class OtpLogin implements OnInit, OnDestroy {
  maskedEmail = 'jo***@gmail.com';
  mfaSessionId = '';
  resendCountdown = 60;
  isLoading = false;
  errorMessage: string | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  private fb = inject(FormBuilder);
  private router = inject(Router);
  private loginService = inject(LoginService);
  private notificationService = inject(NotificationService);
  private profileService = inject(DoctorProfileService);
  private permissionService = inject(PermissionService);
  private clinicService = inject(ClinicService);

  otpForm = this.fb.group({
    otp: [
      '',
      [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(5),
        Validators.pattern(/^[0-9]{5}$/),
      ],
    ],
  });

  ngOnInit(): void {
    const email = sessionStorage.getItem('mfa_email');
    const sessionId = sessionStorage.getItem('mfa_session_id');

    if (email) {
      this.maskedEmail = this.maskEmail(email);
    }

    if (sessionId) {
      this.mfaSessionId = sessionId;
    }

    this.startCountdown();
  }

  ngOnDestroy(): void {
    this.clearCountdown();
  }

  private maskEmail(email: string): string {
    const [name, domain] = email.split('@');
    if (!name || !domain) {
      return email;
    }

    const visible = name.slice(0, 2);
    return `${visible}${'*'.repeat(Math.max(name.length - 2, 2))}@${domain}`;
  }

  private startCountdown(): void {
    this.clearCountdown();
    this.resendCountdown = 60;
    this.countdownTimer = setInterval(() => {
      if (this.resendCountdown <= 1) {
        this.resendCountdown = 0;
        this.clearCountdown();
        return;
      }
      this.resendCountdown -= 1;
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  resendOtp() {
    if (this.resendCountdown > 0) {
      return;
    }

    this.errorMessage = null;
    if (!this.mfaSessionId) {
      const msg = 'Missing MFA session details. Please log in again.';
      this.errorMessage = msg;
      this.notificationService.error(msg, 'Session Expired');
      this.router.navigate(['/']);
      return;
    }

    this.loginService.resendOtp(this.mfaSessionId).subscribe({
      next: () => {
        this.notificationService.success('A new verification code has been sent.', 'Code Sent');
        this.startCountdown();
      },
      error: (error) => {
        const msg =
          error?.error?.message ?? 'Failed to resend the verification code. Please try again.';
        this.errorMessage = msg;
        this.notificationService.error(msg, 'Resend Failed');
      },
    });
  }

  verifyOtp() {
    this.errorMessage = null;
    if (!this.otpForm.valid) {
      this.otpForm.markAllAsTouched();
      return;
    }

    const otpValue = this.otpForm.get('otp')?.value;
    if (!otpValue || !this.mfaSessionId) {
      const msg = 'Missing MFA session details. Please log in again.';
      this.errorMessage = msg;
      this.notificationService.error(msg, 'Session Expired');
      this.router.navigate(['/']);
      return;
    }

    this.isLoading = true;
    this.loginService.verifyMfa({ sessionId: this.mfaSessionId, otp: otpValue }).subscribe({
      next: (res: LoginResponseModel) => {
        this.storeLoginResponse(res);
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        const msg = error?.error?.message ?? 'Failed to verify OTP code. Please check and retry.';
        this.errorMessage = msg;
        this.notificationService.error(msg, 'Verification Failed');
      },
    });
  }

  private storeLoginResponse(res: LoginResponseModel): void {
    if (res.idToken) {
      sessionStorage.setItem('access_token', res.idToken);
    }
    if (res.refreshToken) {
      localStorage.setItem('refresh_token', res.refreshToken);
    }

    sessionStorage.removeItem('mfa_session_id');
    sessionStorage.removeItem('mfa_email');

    this.getProfileDetails();
  }

  private getProfileDetails(): void {
    this.profileService.getUserProfile().subscribe({
      next: (res) => {
        localStorage.setItem('userProfile', JSON.stringify(res));
        localStorage.setItem('availableUserRoles', JSON.stringify(res.roles));
        if (res.clinicId) {
          localStorage.setItem('clinicId', res.clinicId);
          localStorage.setItem('activeClinicId', res.clinicId);
          sessionStorage.setItem('clinicId', res.clinicId);
          sessionStorage.setItem('activeClinicId', res.clinicId);
        }

        if (res.currentActiveRole === 'DOCTOR' || res.roles[0] === 'DOCTOR') {
          localStorage.setItem('userRole', 'DOCTOR');
          this.permissionService.setPermissions(UserRolePermissions[UserRole.DOCTOR]);
          this.getDoctorProfileDetails();
        } else if (res.currentActiveRole === 'MANAGER' || res.roles[0] === 'MANAGER') {
          localStorage.setItem('userRole', 'MANAGER');
          this.permissionService.setPermissions(UserRolePermissions[UserRole.MANAGER]);
          this.router.navigate(['dashboard/manager-dashboard'], {
            state: { managerData: res },
          });
        } else if (res.roles[0] === 'RECEPTION') {
          localStorage.setItem('userRole', 'RECEPTION');
          this.permissionService.setPermissions(UserRolePermissions[UserRole.RECEPTION]);
          this.getClinicDoctorsForReception(res);
        }
      },
      error: (error) => {
        this.isLoading = false;
        const msg = error?.error?.message ?? 'Failed to load user profile.';
        this.errorMessage = msg;
        this.notificationService.error(msg, 'Profile Error');
      },
    });
  }

  private getClinicDoctorsForReception(receptionProfile: any): void {
    this.clinicService.getClinicDoctorProfiles().subscribe({
      next: (doctors) => {
        localStorage.setItem('clinicDoctors', JSON.stringify(doctors));
        this.router.navigate(['dashboard/reception-dashboard'], {
          state: { receptionData: receptionProfile },
        });
      },
      error: () => {
        this.router.navigate(['dashboard/reception-dashboard'], {
          state: { receptionData: receptionProfile },
        });
      },
    });
  }

  private getDoctorProfileDetails(): void {
    this.profileService.getDoctorProfileDetails().subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.profileStatus === DoctorProfileStatus.DOCTOR_NOT_ONBOARDED) {
          localStorage.setItem('doctorLoad', JSON.stringify(res));
          this.router.navigate(['dashboard/doctor-onboard']);
        } else {
          this.router.navigate(['dashboard/doctor-dashboard'], {
            state: { doctorData: res },
          });
        }
      },
      error: (error) => {
        this.isLoading = false;
        const msg = error?.error?.message ?? 'Failed to load doctor profile.';
        this.errorMessage = msg;
        this.notificationService.error(msg, 'Doctor Profile Error');
      },
    });
  }
}
